import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthEndPoints } from '../helper/apiconstant';
import { environment } from '../environments/environment';
import { BackendMenuJsonItem } from '../pages/structure/sidebar/sidebar.interfaces';
import { ManageSession, StoredProfileDetails } from './manage-session';
import { SessionService } from './session.service';
import { ServerTimeService } from './server-time';
import { SocketService } from './socket.service';

type ToastType = 'success' | 'error';

@Injectable({
  providedIn: 'root',
})
export class Commonservice {
  private readonly isLoggedInState = signal(false);
  private readonly authReadyState = signal(false);
  private readonly toastDurationMs = 4000;
  private toastHost: HTMLElement | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly sessionSvc: ManageSession,
    private readonly appSessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly socketSvc: SocketService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
  ) {}

  get isLoggedIn(): boolean {
    return this.isLoggedInState();
  }

  get authReady(): boolean {
    return this.authReadyState();
  }

  setLoggedIn(value: boolean): void {
    this.isLoggedInState.set(value);
  }

  setAuthContext(
    token: string,
    menuJson?: BackendMenuJsonItem[] | null,
    isVerified?: boolean | null,
    profileDetails?: StoredProfileDetails | null,
  ): void {
    this.sessionSvc.saveAuthToken(token);
    this.appSessionSvc.setSessionContext({
      menuJson: menuJson ?? null,
      isVerified: isVerified ?? null,
      profileDetails: profileDetails ?? null,
    });
    this.setLoggedIn(true);
    this.socketSvc.connect(environment.socketUrl);
  }

  async initializeAuthState(): Promise<void> {
    this.authReadyState.set(false);

    const token = this.sessionSvc.getAuthToken();
    if (!token) {
      this.clearSession();
      this.authReadyState.set(true);
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.apiBaseURL}${AuthEndPoints.VERIFYTOKEN}`, { token }),
      );

      const isValid =
        response?.status === 'TRUE' &&
        (response?.value?.valid === true || Boolean(response?.value?.token || response?.value?.user_token));

      if (isValid) {
        const menuJson = await this.fetchMenuJson();
        this.serverTimeSvc.initialize(response?.value?.current_time, response?.value?.current_time_bs);
        this.setLoggedIn(true);
        this.appSessionSvc.setSessionContext({
          menuJson,
          isVerified: this.extractVerificationState(response?.value),
          profileDetails: this.extractProfileDetails(response?.value),
        });
        this.socketSvc.connect(environment.socketUrl);
        this.authReadyState.set(true);
        return;
      }

      this.clearInvalidSession();
    } catch {
      this.clearInvalidSession();
    } finally {
      this.authReadyState.set(true);
    }
  }

  clearSession(): void {
    this.serverTimeSvc.reset();
    this.socketSvc.disconnect();
    this.sessionSvc.clearAuthToken();
    this.appSessionSvc.clearSession();
    this.setLoggedIn(false);
  }

  private async fetchMenuJson(): Promise<BackendMenuJsonItem[] | null> {
    const response = await firstValueFrom(
      this.http.get<any>(`${environment.apiBaseURL}${AuthEndPoints.MENU_JSON}`),
    );

    return this.extractMenuJson(response?.value);
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post<any>(`${environment.apiBaseURL}${AuthEndPoints.LOGOUT}`, {}));
    } catch {
      // The user still needs to be signed out locally if the backend logout request fails.
    } finally {
      this.clearSession();
    }
  }

  showSuccessToastr(message: string): void {
    this.showToast(message, 'success');
  }

  showErrorToastr(message: string): void {
    this.showToast(message, 'error');
  }

  notifyApiResponse(response: unknown, fallbackSuccess = 'Operation completed successfully', showSuccessToastr:boolean=false): void {
    const { message, type } = this.resolveToastPayload(response, fallbackSuccess);
    if (!message) {
      return;
    }

    if (type === 'error') {
      this.showErrorToastr(message);
      return;
    }
    if (showSuccessToastr) this.showSuccessToastr(message);
  }

  notifyApiError(error: unknown, fallbackError = 'Unable to complete the request'): void {
    const message = this.resolveErrorMessage(error, fallbackError);
    if (message) {
      this.showErrorToastr(message);
    }
  }

  private clearInvalidSession(): void {
    this.clearSession();
  }

  private showToast(message: string, type: ToastType): void {
    const normalizedMessage = typeof message === 'string' ? message.trim() : '';
    if (!normalizedMessage || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const toastHost = this.getToastHost();
    const toast = this.document.createElement('div');
    toast.className = `app-toast app-toast--${type}`;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');

    const text = this.document.createElement('span');
    text.className = 'app-toast__message';
    text.textContent = normalizedMessage;

    const closeButton = this.document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'app-toast__close';
    closeButton.setAttribute('aria-label', 'Dismiss notification');
    closeButton.textContent = '×';

    const dismiss = (): void => {
      if (!toast.isConnected) {
        return;
      }
      toast.classList.remove('app-toast--visible');
      window.setTimeout(() => toast.remove(), 180);
    };

    closeButton.addEventListener('click', dismiss);
    toast.append(text, closeButton);
    toastHost.appendChild(toast);

    window.setTimeout(() => {
      toast.classList.add('app-toast--visible');
    }, 0);

    window.setTimeout(dismiss, this.toastDurationMs);
  }

  private getToastHost(): HTMLElement {
    if (this.toastHost?.isConnected) {
      return this.toastHost;
    }

    const existingHost = this.document.querySelector('.app-toast-host');
    if (existingHost instanceof HTMLElement) {
      this.toastHost = existingHost;
      return existingHost;
    }

    const host = this.document.createElement('div');
    host.className = 'app-toast-host';
    this.document.body.appendChild(host);
    this.toastHost = host;
    return host;
  }

  private resolveToastPayload(response: unknown, fallbackSuccess: string): { message: string; type: ToastType } {
    if (!response || typeof response !== 'object') {
      return { message: '', type: 'success' };
    }

    const record = response as Record<string, unknown>;
    const status = typeof record['status'] === 'string' ? record['status'].toUpperCase() : '';
    const message = typeof record['msg'] === 'string' ? record['msg'].trim() : '';

    if (status === 'FALSE') {
      return { message, type: 'error' };
    }

    if (status === 'TRUE') {
      return { message: message || fallbackSuccess, type: 'success' };
    }

    return { message, type: 'success' };
  }

  private resolveErrorMessage(error: unknown, fallbackError: string): string {
    if (!error || typeof error !== 'object') {
      return fallbackError;
    }

    const record = error as Record<string, unknown>;
    const nestedError = record['error'];
    if (nestedError && typeof nestedError === 'object') {
      const nestedRecord = nestedError as Record<string, unknown>;
      const nestedMessage = typeof nestedRecord['msg'] === 'string' ? nestedRecord['msg'].trim() : '';
      if (nestedMessage) {
        return nestedMessage;
      }
    }

    const message = typeof record['msg'] === 'string' ? record['msg'].trim() : '';
    if (message) {
      return message;
    }

    const errorMessage = typeof record['message'] === 'string' ? record['message'].trim() : '';
    return errorMessage || fallbackError;
  }

  private extractMenuJson(value: unknown): BackendMenuJsonItem[] | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidate = (value as { menu_json?: unknown }).menu_json;
    return Array.isArray(candidate) ? (candidate as BackendMenuJsonItem[]) : null;
  }

  private extractVerificationState(value: unknown): boolean | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const claims = (value as { claims?: { is_verified?: unknown } }).claims;
    if (!claims || typeof claims !== 'object') {
      return null;
    }

    return typeof claims.is_verified === 'boolean' ? claims.is_verified : null;
  }

  private extractProfileDetails(value: unknown): StoredProfileDetails | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;
    const userId = this.toNullableString(record['user_id']);
    const username = typeof record['username'] === 'string' ? record['username'] : null;
    const userFullname = typeof record['user_fullname'] === 'string' ? record['user_fullname'] : null;
    const email = typeof record['email'] === 'string' ? record['email'] : null;
    const mobileNo = typeof record['mobile_no'] === 'string' ? record['mobile_no'] : null;
    const roleName = typeof record['role_name'] === 'string' ? record['role_name'] : null;
    const roleDisplayName = typeof record['role_display_name'] === 'string' ? record['role_display_name'] : null;
    const substationId = this.toNullableNumber(record['substation_id'] ?? (record['claims'] as Record<string, unknown> | undefined)?.['substation_id']);
    const generatorId = this.toNullableNumber(record['generator_id'] ?? (record['claims'] as Record<string, unknown> | undefined)?.['generator_id']);
    const profileImage = record['profile_image'];

    const normalizedProfileImage =
      profileImage && typeof profileImage === 'object'
        ? {
            id: this.toNullableNumber((profileImage as Record<string, unknown>)['id']),
            filename: this.toNullableString((profileImage as Record<string, unknown>)['filename']),
            filepath: this.toNullableString((profileImage as Record<string, unknown>)['filepath']),
            uploaded_file: this.toNullableString((profileImage as Record<string, unknown>)['uploaded_file']),
          }
        : null;

    if (!userFullname && !normalizedProfileImage) {
      return null;
    }

    return {
      user_id: userId,
      username,
      user_fullname: userFullname,
      email,
      mobile_no: mobileNo,
      role_name: roleName,
      role_display_name: roleDisplayName,
      substation_id: substationId,
      generator_id: generatorId,
      profile_image: normalizedProfileImage,
    };
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toNullableString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value : null;
  }
}
